import { useState, useEffect } from 'react';
import { api } from '@/lib/axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface Notification {
  _id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  link?: string;
  createdAt: string;
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Simular notificaciones para demostración
  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      // En una implementación real, esto sería una llamada a la API
      // const response = await api.get('/api/notifications');
      // setNotifications(response.data);
      
      // Simulación para demostración
      setTimeout(() => {
        const demoNotifications: Notification[] = [
          {
            _id: '1',
            userId: user?.id || '',
            title: 'Cambio de precio',
            message: 'Una propiedad en tu lista de favoritos ha bajado de precio de $250,000 a $230,000.',
            type: 'info',
            read: false,
            link: '/dashboard/favorites',
            createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString() // 30 minutos atrás
          },
          {
            _id: '2',
            userId: user?.id || '',
            title: 'Propiedad vendida',
            message: 'Una propiedad que tenías en favoritos ha sido vendida. Ya no está disponible.',
            type: 'warning',
            read: false,
            link: '/dashboard/favorites',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString() // 2 horas atrás
          },
          {
            _id: '3',
            userId: user?.id || '',
            title: 'Nueva propiedad similar',
            message: 'Hemos encontrado una propiedad similar a tus favoritos que podría interesarte.',
            type: 'success',
            read: true,
            link: '/',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() // 1 día atrás
          },
          {
            _id: '4',
            userId: user?.id || '',
            title: 'Mensaje nuevo',
            message: 'Has recibido un nuevo mensaje sobre una de tus propiedades.',
            type: 'info',
            read: true,
            link: '/dashboard/messages',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString() // 2 días atrás
          },
          {
            _id: '5',
            userId: user?.id || '',
            title: 'Recordatorio de visita',
            message: 'Tienes una visita programada para mañana a las 15:00.',
            type: 'info',
            read: true,
            link: '/dashboard/visits',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString() // 3 días atrás
          }
        ];
        
        setNotifications(demoNotifications);
        setIsLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Error al cargar notificaciones');
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      // En una implementación real, esto sería una llamada a la API
      // await api.put(`/api/notifications/${id}/read`);
      
      // Actualizar localmente
      setNotifications(
        notifications.map(notification => 
          notification._id === id 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      toast.success('Notificación marcada como leída');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Error al marcar notificación como leída');
    }
  };

  const markAllAsRead = async () => {
    try {
      // En una implementación real, esto sería una llamada a la API
      // await api.put('/api/notifications/read-all');
      
      // Actualizar localmente
      setNotifications(
        notifications.map(notification => ({ ...notification, read: true }))
      );
      
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Error al marcar todas las notificaciones como leídas');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }
    
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'info':
        return '🔵';
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return '📢';
    }
  };

  const formatNotificationDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "d 'de' MMMM 'a las' HH:mm", { locale: es });
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !notification.read;
    if (activeTab === 'read') return notification.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Notificaciones</h1>
          <p className="text-muted-foreground">
            Gestiona tus notificaciones y mantente al día con tus propiedades
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAllAsRead}>
            Marcar todas como leídas
          </Button>
        )}
      </div>
      
      <Separator />
      
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">
            Todas
            <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
              {notifications.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="unread">
            No leídas
            <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs">
              {unreadCount}
            </span>
          </TabsTrigger>
          <TabsTrigger value="read">
            Leídas
            <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
              {notifications.filter(n => n.read).length}
            </span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="mt-0">
          {renderNotificationList(filteredNotifications)}
        </TabsContent>
        
        <TabsContent value="unread" className="mt-0">
          {renderNotificationList(filteredNotifications)}
        </TabsContent>
        
        <TabsContent value="read" className="mt-0">
          {renderNotificationList(filteredNotifications)}
        </TabsContent>
      </Tabs>
    </div>
  );
  
  function renderNotificationList(notifications: Notification[]) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    if (notifications.length === 0) {
      return (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-muted-foreground">No hay notificaciones {activeTab !== 'all' ? 'en esta categoría' : ''}</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {notifications.map((notification) => (
          <div 
            key={notification._id}
            className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
              notification.read ? 'bg-white' : 'bg-blue-50'
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex gap-4">
              <div className="text-2xl">
                {getNotificationIcon(notification.type)}
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <h4 className={`text-base font-medium ${!notification.read ? 'text-black' : 'text-gray-700'}`}>
                    {notification.title}
                  </h4>
                  <span className="text-xs text-gray-500">
                    {formatNotificationDate(notification.createdAt)}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${!notification.read ? 'text-gray-800' : 'text-gray-600'}`}>
                  {notification.message}
                </p>
                {notification.link && (
                  <div className="mt-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-7 px-2 text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(notification.link!);
                      }}
                    >
                      Ver detalles →
                    </Button>
                  </div>
                )}
              </div>
              {!notification.read && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(notification._id);
                  }}
                >
                  Marcar como leída
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
} 